import type React from 'react';
import { useLocalStorage } from '@uidotdev/usehooks';
import Tooltip from '@components/Presentation/atoms/Tooltip';
import { QuestionIcon, PlusIcon, MinusIcon } from '@phosphor-icons/react';
import { useSelectedSlide } from '@components/Presentation/hooks/useSelectedSlide';

interface SettingBlockProps {
  title?: string;
  titleMargin?: 'sm' | 'md' | 'lg';
  itemGap?: 'sm' | 'md' | 'lg';
  hasBorder?: boolean;
  hasBorderTop?: boolean;
  hasBorderDown?: boolean;
  hasBg?: boolean;
  tooltip?: React.ReactNode;
  addPadding?: boolean;
  children: React.ReactNode;
  rightContent?: React.ReactNode;
  className?: string;
  titleCss?: string;
  isAccordion?: boolean;
  defaultExpanded?: boolean;
  startWith?: React.ReactNode;
  endWith?: React.ReactNode;
}
function SettingBlock({
  title = '',
  titleMargin = 'md',
  itemGap = 'md',
  children,
  hasBorderTop = true,
  hasBorderDown = false,
  hasBg = false,
  tooltip = '',
  addPadding = true,
  rightContent = null,
  className = '',
  titleCss = '',
  isAccordion,
  defaultExpanded = false,
  startWith,
  endWith
}: SettingBlockProps) {
  const selectedSlide = useSelectedSlide();

  // Auto-generate storage key from slide type + title (unique per interaction)
  const storageKey = `settingBlock-${selectedSlide.type}-${title.toLowerCase().replace(/\s+/g, '-')}`;

  // Choose which state to use based on whether localStorage is available
  const [isExpanded, setIsExpanded] = useLocalStorage(storageKey, defaultExpanded);

  // Auto-enable accordion when title has a value
  const shouldBeAccordion = isAccordion !== undefined ? isAccordion : !!title;

  const gap = {
    sm: 'gap-3',
    md: 'gap-4',
    lg: 'gap-6'
  };
  const margin = {
    sm: 'mb-3',
    md: 'mb-4',
    lg: 'mb-6'
  };

  const toggleAccordion = () => {
    if (shouldBeAccordion) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      className={`flex flex-col ${hasBorderTop ? 'border-t border-divider' : ''
      }
      ${hasBorderDown ? 'border-b border-divider' : ''}
      ${hasBg ? '' : ''}
      ${className}`}
    >
      {title && shouldBeAccordion ? (
        <div
          className={`flex justify-between items-center py-6 ${addPadding ? 'px-6' : ''} cursor-pointer select-none`}
          onClick={toggleAccordion}
        >
          <h4
            className={`text-sm font-semibold flex items-center gap-2 leading-5 flex-1 text-default-900 ${titleCss}`}
          >
            {startWith}
            {title}
            {endWith}
            {tooltip && (
              <Tooltip content={tooltip}>
                <QuestionIcon
                  size={20}
                  className='!text-default-400'
                />
              </Tooltip>
            )}
          </h4>
          {rightContent && rightContent}
          <button
            type="button"
            className="flex items-center justify-center ml-auto cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              toggleAccordion();
            }}
          >
            {isExpanded ? (
              <MinusIcon size={20} className="text-default-700" />
            ) : (
              <PlusIcon size={20} className="text-default-700" />
            )}
          </button>
        </div>
      ) : title ? (
        <div className={`flex justify-between items-start ${addPadding ? 'py-6 px-6' : 'py-6'}`}>
          <h4
            className={`text-sm font-semibold flex items-center gap-2 leading-5 flex-1 text-default-900 ${titleCss} ${margin[titleMargin]}`}
          >
            {title}
            {tooltip && (
              <Tooltip content={tooltip}>
                <QuestionIcon
                  size={20}
                  className='!text-default-400'
                />
              </Tooltip>
            )}
          </h4>
          {rightContent && rightContent}
        </div>
      ) : null}

      {(!shouldBeAccordion || isExpanded) && (
        <div className={`flex flex-col ${gap[itemGap]} ${addPadding ? 'px-6 pb-6' : 'pb-6'} ${!title ? 'pt-6' : ''}`}>{children}</div>
      )}
    </div>
  );
}

export default SettingBlock;
